import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useBillingState } from '@/state/billing/useBillingState';
import { ONBOARDING_KEY } from '@/state/onboarding/useOnboarding';
import { AuthHeading, AuthSecondaryButton } from '../Authentication/AuthControls';
import { OnboardingFrame } from './plan/OnboardingFrame';

type ActivatingStepProps = {
  /** The wait has run its full length without the subscription arriving. */
  waitedTooLong: boolean;
};

/**
 * Between paying at Polar and our copy of the subscription arriving, in place of the plan picker.
 * Showing the picker there leaves a live "start your trial" button on screen for someone who has
 * already paid, and pressing it is refused as "already subscribed". After a long wait it says the
 * payment is received and offers to check again; the page also keeps no state of its own, so a
 * reload is always safe.
 */
export const ActivatingStep = ({ waitedTooLong }: ActivatingStepProps) => {
  const queryClient = useQueryClient();
  // Keeps asking, slowly, after the main wait has given up: the subscription may still arrive.
  const { billingState } = useBillingState({ refetchInterval: 5000 });
  const isLive = billingState?.agencyFee ? billingState.agencyFee.live : !!billingState?.hasLiveSubscription;

  useEffect(() => {
    if (isLive) checkAgain();
  }, [isLive]);

  const checkAgain = () => {
    queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    queryClient.invalidateQueries({
      predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('BILLING_'),
    });
  };

  return (
    <OnboardingFrame>
      <div className="flex max-w-lg flex-col gap-5">
        <AuthHeading
          title={waitedTooLong ? 'Payment received.' : 'Activating your subscription...'}
          subtitle={
            waitedTooLong
              ? 'Your subscription is taking longer than usual to show up here. Nothing is lost: it can take a minute. Check again, or reload this page.'
              : 'One moment while we confirm it with our payment provider.'
          }
        />
        {waitedTooLong ? (
          <div className="w-fit">
            <AuthSecondaryButton type="button" onClick={checkAgain}>
              Check again
            </AuthSecondaryButton>
          </div>
        ) : (
          <Loader2 className="text-dim size-5 animate-spin" aria-hidden="true" />
        )}
      </div>
    </OnboardingFrame>
  );
};
